import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Divider, Typography } from "@mui/material";
import {
  DataGrid,
  type GridCellParams,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowId,
} from "@mui/x-data-grid";
import Autocomplete from "@components/Autocomplete/Autocomplete";
import { dataGridSx, dataGridWrapperSx } from "@components/DataTable/styles";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { usePortServicesFlow } from "@features/vessel-call/vessel-call-status/PortServices/flow/context/PortServicesFlowContext";
import type { ChangeStatusDrawerInfo } from "@features/vessel-call/vessel-call-status/PortServices/hoc/withDrawers";
import {
  getWasteFormSchema,
  type WasteFormSchema,
} from "@features/vessel-call/vessel-call-status/PortServices/schema/portServicesFormSchema";
import { type MarpolAnnexI } from "@features/vessel-call/vessel-call-status/PortServices/store/usePortServicesForm.store.ts";
import { useTranslation } from "react-i18next";

const TABLE_FIELDS = Object.freeze({
  marpolAnnexIOil: "marpolAnnexIOil",
  quantityM3: "quantityM3",
  action: "action",
});

const LANG_BASE = "vessel-call.marpol-annex1";
const LANG_BASE_HEADER = `${LANG_BASE}.grid-headers`;
const LANG_BASE_SIDEBAR = `${LANG_BASE}.sidebar`;
const TEST_ID_BASE = "marpol-annex";

type MarpolOption = {
  value: string;
  label: string;
};

function buildWasteDefaults(src?: Partial<WasteFormSchema>) {
  return {
    marpolAnnexIOil: src?.marpolAnnexIOil ?? "",
    otherSpecifyDetails: src?.otherSpecifyDetails ?? "",
    quantityM3: src?.quantityM3 ?? 0,
  } as const;
}
interface MarpolAnnexProps {
  serviceDetails: ChangeStatusDrawerInfo;
  marpolOptions: MarpolOption[];
}
const MarpolAnnexGrid: React.FC<MarpolAnnexProps> = ({
  serviceDetails: { wasteResiduesForDischargeFacility: currentWasteResiduesForDischargeFacility },
  marpolOptions,
}) => {
  const { t } = useTranslation();
  const {
    setWasteResiduesForDischargeFacility: setFlowWasteResidues,
    errors: flowErrors,
    setErrors: setFlowErrors,
  } = usePortServicesFlow();

  const [wasteResiduesForDischargeFacility, setWasteResiduesForDischargeFacility] = useState<
    MarpolAnnexI[]
  >([]);
  const [drawerWaste, setDrawerWaste] = useState<WasteFormSchema>(buildWasteDefaults());
  const [drawerErrors, setDrawerErrors] = useState<Partial<Record<keyof WasteFormSchema, string>>>(
    {},
  );

  useEffect(() => {
    const parsedValue = Array.isArray(currentWasteResiduesForDischargeFacility)
      ? currentWasteResiduesForDischargeFacility
      : [];

    const validValue = parsedValue.filter(
      (waste): waste is MarpolAnnexI =>
        waste != null &&
        typeof waste.marpolAnnexIOil === "string" &&
        typeof waste.otherSpecifyDetails === "string" &&
        typeof waste.quantityM3 === "number" &&
        Number.isFinite(waste.quantityM3),
    );

    setWasteResiduesForDischargeFacility(validValue);
    setFlowWasteResidues(validValue);
  }, [currentWasteResiduesForDischargeFacility, setWasteResiduesForDischargeFacility]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingWaste, setEditingWaste] = useState<MarpolAnnexI | null>(null);

  const isEditing = editingWaste !== null;
  const isOtherSpecifySelected = drawerWaste.marpolAnnexIOil === "Other (Specify)";

  const tableRows = useMemo(() => {
    return (wasteResiduesForDischargeFacility ?? []).map((waste, idx) => ({
      ...waste,
      id: waste.id ?? `waste-${idx}`,
      updateAction: "UPDATE",
      marpolAnnexIOil:
        waste.marpolAnnexIOil === "Other (Specify)"
          ? (waste.otherSpecifyDetails ?? "")
          : waste.marpolAnnexIOil,
    }));
  }, [wasteResiduesForDischargeFacility]);

  const fieldErrorMessage = flowErrors.wasteResiduesForDischargeFacility ?? "";

  const openEditDrawer = useCallback(
    (rowIndex: number) => {
      const row = wasteResiduesForDischargeFacility?.[rowIndex];
      if (!row) return;
      setEditingWaste(row);
      setIsDrawerOpen(true);
    },
    [wasteResiduesForDischargeFacility],
  );

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  useEffect(() => {
    if (!isDrawerOpen) return;

    const initialValues = isEditing
      ? {
          marpolAnnexIOil: editingWaste?.marpolAnnexIOil ?? "",
          otherSpecifyDetails: editingWaste?.otherSpecifyDetails ?? "",
          quantityM3: editingWaste?.quantityM3 ?? 0,
        }
      : buildWasteDefaults();

    setDrawerWaste(initialValues);
    setDrawerErrors({});
  }, [editingWaste, isDrawerOpen, isEditing]);

  const syncWastes = useCallback(
    (next: MarpolAnnexI[]) => {
      setWasteResiduesForDischargeFacility(next);
      setFlowWasteResidues(next);
      setFlowErrors((prev) => ({ ...prev, wasteResiduesForDischargeFacility: undefined }));
    },
    [setFlowErrors, setFlowWasteResidues],
  );

  const onRowEdit = useCallback(
    (rowId: GridRowId) => {
      const idx = tableRows.findIndex((row) => String(row.id) === String(rowId));
      if (idx < 0) return;
      openEditDrawer(idx);
    },
    [openEditDrawer, tableRows],
  );

  const handleActionCellClick = useCallback(
    (params: GridCellParams<(typeof tableRows)[number]>) => {
      if (params.field !== TABLE_FIELDS.action) return;
      onRowEdit(params.id);
    },
    [onRowEdit],
  );

  const columns = useMemo<GridColDef<(typeof tableRows)[number]>[]>(
    () => [
      {
        field: TABLE_FIELDS.marpolAnnexIOil,
        headerName: t(`${LANG_BASE_HEADER}.marpolAnnexIOil`),
        flex: 1,
        minWidth: 180,
      },
      {
        field: TABLE_FIELDS.quantityM3,
        headerName: t(`${LANG_BASE_HEADER}.quantityM3`),
        flex: 0.7,
        minWidth: 110,
      },
      {
        field: TABLE_FIELDS.action,
        headerName: t(`${LANG_BASE_HEADER}.action`),
        width: 80,
        minWidth: 80,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams<(typeof tableRows)[number]>) => (
          <Typography
            component="button"
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onRowEdit(params.id);
            }}
            sx={(theme) => ({
              color: theme.palette.primary.main,
              fontWeight: theme.typography.fontWeightBold,
              background: "transparent",
              border: 0,
              cursor: "pointer",
              padding: 0,
            })}
          >
            {t(`${LANG_BASE_HEADER}.update`)}
          </Typography>
        ),
      },
    ],
    [onRowEdit, t],
  );

  const handleSubmitDrawer = useCallback(() => {
    const parsedQuantity = Number(drawerWaste.quantityM3);

    const waste: MarpolAnnexI = {
      ...(isEditing && editingWaste?.id ? { id: editingWaste.id } : {}),
      marpolAnnexIOil: drawerWaste.marpolAnnexIOil ?? "",
      otherSpecifyDetails: drawerWaste.otherSpecifyDetails ?? "",
      quantityM3: Number.isFinite(parsedQuantity) ? parsedQuantity : 0,
    };

    if (isEditing && editingWaste != null) {
      const next = (wasteResiduesForDischargeFacility ?? []).map((w) =>
        w === editingWaste ? waste : w,
      );
      syncWastes(next);
      setEditingWaste(null);
      setIsDrawerOpen(false);
      return;
    }

    syncWastes([...(wasteResiduesForDischargeFacility ?? []), waste]);
    setIsDrawerOpen(false);
  }, [drawerWaste, editingWaste, isEditing, syncWastes, wasteResiduesForDischargeFacility]);

  const handleCancelDrawer = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  const handleDrawerSubmit = useCallback(() => {
    const result = getWasteFormSchema(t).safeParse(drawerWaste);

    if (!result.success) {
      const nextErrors: Partial<Record<keyof WasteFormSchema, string>> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof WasteFormSchema;
        if (!key) continue;
        nextErrors[key] = issue.message;
      }
      setDrawerErrors(nextErrors);
      return;
    }

    setDrawerErrors({});
    handleSubmitDrawer();
    setDrawerWaste(buildWasteDefaults());
  }, [drawerWaste, handleSubmitDrawer, t]);

  return (
    <div className="flex flex-col items-end gap-4" data-testid={TEST_ID_BASE}>
      <div className="flex flex-col w-full gap-4">
        <Typography variant="subtitle1">{t(`${LANG_BASE}.title`)}</Typography>
        <Box
          sx={{
            ...dataGridWrapperSx,
            width: "100%",
            minHeight: 180,
            height: Math.max(180, tableRows.length * 52 + 56),
          }}
        >
          <DataGrid
            rows={tableRows}
            columns={columns}
            onCellClick={handleActionCellClick}
            hideFooter
            disableColumnMenu
            disableColumnSorting
            disableColumnResize
            getRowId={(row) => row.id}
            sx={(theme) => ({
              ...(typeof dataGridSx === "function" ? dataGridSx(theme) : {}),
              "& .MuiDataGrid-row:hover": {
                cursor: "default",
              },
            })}
          />
        </Box>
      </div>

      <Divider className="w-full" />

      <div className="flex w-full justify-between items-center">
        <Typography color="error" variant="caption">
          {fieldErrorMessage && String(fieldErrorMessage)}
        </Typography>
      </div>

      {isDrawerOpen && (
        <SideDrawer
          isOpen={isDrawerOpen}
          isChildDrawer
          onClose={closeDrawer}
          onCancel={handleCancelDrawer}
          onSubmit={handleDrawerSubmit}
          title={t(`${LANG_BASE_SIDEBAR}.${isEditing ? "title-edit" : "title-add"}`)}
          submitLabel={t(`${LANG_BASE_SIDEBAR}.buttons.${isEditing ? "edit" : "add"}`)}
        >
          <div className="flex flex-col gap-3 px-6">
            <div className="label-and-input flex flex-col gap-2">
              <CustomFormLabel data-testid="labelId">
                {t("vessel-call.marpol-annex1.grid-headers.marpolAnnexIOil")}
              </CustomFormLabel>
              <Autocomplete
                disabled
                options={marpolOptions}
                value={
                  drawerWaste.marpolAnnexIOil
                    ? (marpolOptions.find(
                        (o) => String(o.value) === String(drawerWaste.marpolAnnexIOil),
                      ) ?? null)
                    : null
                }
                onChange={(opt) => {
                  const nextValue = opt?.value == null ? "" : String(opt.value);
                  setDrawerWaste((prev) => ({ ...prev, marpolAnnexIOil: nextValue }));
                  setDrawerErrors((prev) => ({ ...prev, marpolAnnexIOil: undefined }));

                  if (nextValue !== "Other (Specify)") {
                    setDrawerWaste((prev) => ({ ...prev, otherSpecifyDetails: "" }));
                    setDrawerErrors((prev) => ({ ...prev, otherSpecifyDetails: undefined }));
                  }

                  if (opt?.value == null) {
                    return;
                  }
                }}
                testId={`${TEST_ID_BASE}-waste-marpolAnnexIOil-input`}
                placeholder={t("vessel-call.marpol-annex1.grid-headers.marpolAnnexIOil")}
                maxHeight={200}
                error={!!drawerErrors.marpolAnnexIOil}
              />
              {drawerErrors.marpolAnnexIOil && (
                <Typography color="error" variant="caption">
                  {drawerErrors.marpolAnnexIOil}
                </Typography>
              )}
            </div>

            {isOtherSpecifySelected && (
              <div className="label-and-input flex flex-col gap-2">
                <CustomFormLabel data-testid="labelId">
                  {t("vessel-call.marpol-annex1.grid-headers.otherSpecify")}
                </CustomFormLabel>
                <Input
                  disabled
                  error={!!drawerErrors.otherSpecifyDetails}
                  placeholder={t("vessel-call.marpol-annex1.grid-headers.otherSpecify")}
                  id={`${TEST_ID_BASE}-waste-otherSpecify-input`}
                  value={drawerWaste.otherSpecifyDetails}
                  onChange={(e) => {
                    setDrawerWaste((prev) => ({ ...prev, otherSpecifyDetails: e.target.value }));
                    setDrawerErrors((prev) => ({ ...prev, otherSpecifyDetails: undefined }));
                  }}
                  testId={`${TEST_ID_BASE}-waste-otherSpecify-input`}
                  style={drawerErrors.otherSpecifyDetails ? { borderColor: "#d32f2f" } : {}}
                />
                {drawerErrors.otherSpecifyDetails && (
                  <Typography color="error" variant="caption">
                    {drawerErrors.otherSpecifyDetails}
                  </Typography>
                )}
              </div>
            )}

            <div className="label-and-input flex flex-col gap-2">
              <CustomFormLabel data-testid="labelId">
                {t("vessel-call.marpol-annex1.grid-headers.quantityM3")}
              </CustomFormLabel>
              <Input
                type="text"
                error={!!drawerErrors.quantityM3}
                placeholder={t("vessel-call.marpol-annex1.grid-headers.quantityM3")}
                id={`${TEST_ID_BASE}-waste-quantityM3-input`}
                value={String(drawerWaste.quantityM3 ?? "")}
                slotProps={{ htmlInput: { inputMode: "numeric", pattern: "[0-9]*", maxLength: 9 } }}
                onChange={(e) => {
                  const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 9);
                  const next = digitsOnly === "" ? 0 : Number(digitsOnly);
                  setDrawerWaste((prev) => ({ ...prev, quantityM3: next }));
                  setDrawerErrors((prev) => ({ ...prev, quantityM3: undefined }));
                }}
                testId={`${TEST_ID_BASE}-waste-quantityM3-input`}
                style={drawerErrors.quantityM3 ? { borderColor: "#d32f2f" } : {}}
              />
              {drawerErrors.quantityM3 && (
                <Typography color="error" variant="caption">
                  {drawerErrors.quantityM3}
                </Typography>
              )}
            </div>
          </div>
        </SideDrawer>
      )}
    </div>
  );
};

export default MarpolAnnexGrid;
